import React, { useEffect, useState } from 'react'
import { Button, CircularProgress, Box, Typography, Alert, Tooltip } from '@mui/material'
import TemplateComponentsSectionTable from './TemplateComponentsSectionTable'
import { useEditTemplate } from './EditTemplateContext'
import { VariableAllocationTable } from 'types/comp-allocations'

const TemplateComponentsSection: React.FC = () => {
  const {
    period,
    organizationIds,
    allocationTables,
    setAllocationTables,
    updateAllocationTable,
    isLoading,
    organizationMap,
    loadTemplateError,
  } = useEditTemplate()

  const [loadingTimeout, setLoadingTimeout] = useState(false)

  // Add timeout for loading state
  useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        setLoadingTimeout(true)
      }, 10000) // 10 seconds timeout

      return () => clearTimeout(timeout)
    } else {
      setLoadingTimeout(false)
    }
  }, [isLoading])

  if (loadTemplateError) {
    return (
      <Box p={2}>
        <Alert severity="error">
          <Typography variant="body1">Failed to load allocation tables. Please try refreshing the page.</Typography>
        </Alert>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" p={2}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading allocation sections...</Typography>
        {loadingTimeout && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Loading is taking longer than expected. Please check your connection.
          </Alert>
        )}
      </Box>
    )
  }

  if (!allocationTables || allocationTables.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="text.secondary">
          No allocation tables found.
        </Typography>
      </Box>
    )
  }

  const handleAddSection = () => {
    const newTable: VariableAllocationTable = {
      id: crypto.randomUUID(),
      label: 'New Section',
      rows: [],
    }
    setAllocationTables([...allocationTables, newTable])
  }

  const handleUpdateTable = (updatedTable: VariableAllocationTable) => {
    updateAllocationTable(updatedTable)
  }

  const handleDeleteTable = (tableId: string | null | undefined) => {
    setAllocationTables(allocationTables.filter((t) => t.id !== tableId))
  }

  return (
    <Box>
      <Box>
        {allocationTables.map((table, index) => (
          <div key={table.id || index}>
            <Box mb={index < allocationTables.length - 1 ? 1 : 0}>
              <TemplateComponentsSectionTable
                table={table}
                updateTable={handleUpdateTable}
                deleteTable={() => handleDeleteTable(table.id)}
                period={period}
                selectedOrganizations={organizationIds}
                organizationMap={organizationMap}
              />
            </Box>
            <Box pt={1}></Box>
          </div>
        ))}
      </Box>
      <Tooltip title="Add Section">
        <Button size="small" variant="outlined" onClick={handleAddSection}>
          Add Section
        </Button>
      </Tooltip>
    </Box>
  )
}

export default TemplateComponentsSection
